// SplatTypes — core data types for the Phase 5 splat field (Set9 §6): ≤8 material indices +
// 8 signed DISTANCES per terrain SAMPLE; the fragment shader evaluates the ≤8 and lerps (plan D2).
// Pure data. Upload: 33×33 RGBA8UI indices (one index per sample) + TWO RGBA32F distance
// DataTextures (slots 0–3 + slots 4–7 — Set7 §9: a single RGBA32F texel carries only 4 lanes, so
// the 4→8 raise splits the distance field into two LINEAR-filtered N×N textures; the index
// texel carries one index per sample, so it is NOT widened), texel = one sample.
import type {ParamValue} from "../@MaterialTypes.js";

/** Max materials blended per fragment — the binding constraint of the whole design (plan §6: cap at 4→8 via Set7 §9, ~150–250 ALU ops/material). GLSL emit + packer both hard-code this. */
export const SPLAT_MAX_MATERIALS = 8;

/** Material selection input evaluated per sample: world XZ, elevation, and slope magnitude (m/m; from the z-up normal — hypot(nx,ny)/max(nz,ε)). Rule authors never touch the AMR grid or solver. */
export interface SplatSampleInput {
	/** World X of the sample. */
	x: number;
	/** World Y of the sample. */
	y: number;
	/** Terrain elevation at the sample (metres). */
	elevation: number;
	/** Slope magnitude at the sample (m/m; 0 = flat, 1 = 45°, ∞ = vertical).  Computed
	 *  from the per-sample normal: nx/ny encode the gradient, nz the up component —
	 *  slope = hypot(nx, ny)/max(nz, ε). */
	slope: number;
}

/** One selection rule on a splat: the criterion assigning the OWNING assignment's recipe to a sample. First matching rule wins per sample (topmost region on top); rules never carry their own recipe. */
export type SplatRule =
	| ElevationBandRule
	| ClosedRingRule
	| OpenCurveRule
	| CliffBandRule;

/** Elevation band: assign the owning recipe to samples with elevation ∈ [min, max] (closed interval — adjacent bands share edges exactly; first-rule-wins resolves boundary ties). */
export interface ElevationBandRule {
	kind: "elevationBand";
	/** Inclusive lower bound (metres). */
	minElevation: number;
	/** Inclusive upper bound (metres). */
	maxElevation: number;
}

/** Closed ring footprint: assign the owning recipe inside the polygon (ray-cast, world-space — reuses ConstraintRasterizer.pointInPolygon). Phase 7's region UI rasterises map-object footprints here. */
export interface ClosedRingRule {
	kind: "closedRing";
	/** World-space polygon (first point implicitly connects to last). */
	polyline: {x: number; y: number}[];
}

/** Open curve stroke: assign the owning recipe within `width` (m) of the polyline (Phase 7+ data entry — e.g. a sand strip along a coastline). */
export interface OpenCurveRule {
	kind: "openCurve";
	/** World-space open polyline. */
	polyline: {x: number; y: number}[];
	/** Half-stroke width (m) — samples within `width` of any segment are assigned. */
	width: number;
}

/** Cliff band: assign the owning recipe where slope ≥ minSlope (steep faces — bare rock on cliffs). Uses the per-sample slope field. */
export interface CliffBandRule {
	kind: "cliffBand";
	/** Inclusive minimum slope (m/m) for the band. */
	minSlope: number;
}

/** A material assignment on the terrain: which recipe + where. Phase 5 reads this directly
 *  (per-map data, not yet DB-persisted — Phase 7 adds Mod_TerrainMaterials). */
export class SplatMaterialAssignment {
	constructor(data?: Partial<SplatMaterialAssignment>) { Object.assign(this, data); }
	id = "";
	name = "";
	recipeID = "";
	/** Per-instance seed combined with the world seed (Phase 7 wires this into
	 *  per-instance param overrides; Phase 5 keeps the recipe defaults). */
	seed = 0;
	rules: SplatRule[] = [];
	/** Per-region param values for the recipe's `regionModulated` params (plan §6).  Keyed by
	 *  param key; the renderer packs these into the per-region params texture (one row per
	 *  region) instead of uniforms — hundreds of regions is a non-issue.  Absent = resolved default. */
	paramModulations: {[paramKey: string]: ParamValue} = {};
}

/** The rasteriser input bundle: assignments + config knobs. */
export interface SplatRasterizeOptions {
	/** Set9-era rule assignments (elevationBand / closedRing / openCurve / cliffBand rules).
	 *  Set12 P2 DELETED the rule system — the rasterizer is driven by the REGION set instead
	 *  (`rasterizeSplatChunk`'s `ctx.regions`); kept optional for backward compat with the
	 *  tests/old call sites.  A non-empty list is IGNORED by the P2 rasterizer (the plan's §6
	 *  migration: assignments re-authored as paths). */
	assignments?: SplatMaterialAssignment[];
	/** Recipe ids the assignments may reference. Unknown ids are a DATA error (a typo must fail at rasterise time, not render black). */
	knownRecipeIDs: string[];
	/** Hard cap on distinct material indices per chunk (≤ SPLAT_MAX_MATERIALS; the assembler emits exactly this many eval slots). */
	maxMaterials: number;
}

/** The rasterised per-chunk result: N×N material indices + per-slot SIGNED DISTANCES (row-major) +
 *  the recipe ids the chunk used (slot 0 = fallback — drives the set-level program cache). */
export interface SplatChunk {
	/** CHUNK_N×CHUNK_N indices into `recipeIDs` (-1 = no material; the packer renormalises weights so the shader never reads a -1). */
	indices: Uint8Array;
	/** CHUNK_N×CHUNK_N×4 signed distances in METRES, one per material plane (d0..d3, row-major),
	 *  NEGATIVE = inside the region (matches the Set12 §3.1 distance representation; clamped to
	 *  ±(feather + noiseAmp) by `clampDistanceForStorage`).  P2 keeps the shader on hard weights:
	 *  `packSplatChunk` thresholds at 0 (weight = d < 0 ? 1 : 0) → visually identical to the
	 *  Set9 hard `1.0` weights (plan §5 P2). */
	weights: Float32Array;
	/** The recipe ids present in this chunk (slot 0 = fallback; length ≤ maxMaterials). */
	recipeIDs: string[];
	/** CHUNK_N×CHUNK_N WINNING-ASSIGNMENT indices (Phase 7, plan §6): the index of the assignment
	 *  whose rule matched this sample (0 = the fallback / no-rule).  Drives the per-region params
	 *  texture: `regionModulated` params fetch their value from row = region instead of a uniform,
	 *  so a region costs a few floats in the DB + one texel fetch.  Absent on chunks rasterised
	 *  before Phase 7 (backward compat). */
	regions?: Uint8Array;
	/** P5 — per-slot EFFECTIVE DOMINANCE (the smin soft-union's gate, plan §4.2 C): slot i's
	 *  value = the MAXIMUM dominance among the regions sharing that slot.  The fragment shader
	 *  smin-unions the WEIGHTS of slots whose slotDominance match (equal-dominance regions meet
	 *  smoothly — the §4.2 A→C extension), and keeps the P3 ordered paint-over between DISTINCT
	 *  dominances.  Slot 0 (the base) is never smin'd (its distance is never consumed) but keeps
	 *  its entry for slot alignment.  Absent → the shader treats every region-slot as distinct
	 *  dominance (the P3 behaviour — backward compat with pre-P5 chunks). */
	slotDominance?: number[];
	/** Set12 followup (2026-08-25) — per-slot RESOLVED FEATHER in metres (the smin soft-union's
	 *  own-feather fix for the "fade-to-wrong-material" bug): slot i's value = the MAXIMUM
	 *  `feather` among the regions sharing that slot.  The fragment shader feathers EACH SLOT
	 *  with its OWN resolved feather (`regionWeight(d, slotFeather[i])`) rather than the winning
	 *  region's single `g_regionFeather`, so a bbox-overlapping-but-far region (e.g. a distant
	 *  water polygon) whose distance is clamped to its own ±`clampAmt` (= feather + noiseAmp,
	 *  which is >= feather) saturates to weight 0 — it no longer bleeds a phantom partial weight
	 *  under the fading top region.
	 *
	 *  2026-08-27 — this is the region's TRUE `feather`, NOT `feather + edgeNoise.amplitude`
	 *  (the previous value).  The old `feather + amp` over-softened feather-0 (hard-edge)
	 *  regions: `slotFeather = amp` made the shader's `feather <= 0` hard-edge branch never fire,
	 *  so noised boundaries rendered with `smoothstep(-amp, +amp, dd)` — a tooth's tip (`dd ≈ 0`)
	 *  only reached weight 0.5 (the "cutoff"/soft-truncated-teeth look), and a far saturated
	 *  distance (clamped to `+amp`) sat at weight ~0.5 → the phantom.  Using the true `feather`
	 *  restores the hard edge for feather-0 regions (crisp full-strength teeth) while STILL
	 *  saturating far distances: `clampAmt >= feather`, so `regionWeight(clampAmt, feather)` = 0
	 *  (the fade-to-wrong-material property holds for any feather >= 0, including 0 via the
	 *  hard-edge branch).  Slot 0 (the base) keeps its entry for slot alignment (never feathered
	 *  — its distance is never consumed).  Absent → the shader falls back to the old
	 *  single-`g_regionFeather` behaviour.  Parallel to `slotDominance`. */
	slotFeather?: number[];
	/** Set12 P5 (2026-08-27) — per-slot EDGE-NOISE amplitude in metres (the "edge noise gets
	 *  cut off by the polygon edge" fix): slot i's value = the MAXIMUM `edgeNoise.amplitude`
	 *  among the regions sharing that slot (0 = no noise on that slot).  The fragment shader
	 *  displaces EACH SLOT's distance with ITS OWN noise (`regionWeight(wx, d, feather,
	 *  slotNoiseAmp[i], slotNoiseScale[i])`) rather than the winning region's single
	 *  `g_regionNoiseAmp` — so a fragment just OUTSIDE the polygon (positive distance, blended
	 *  to the base) still gets the region's warp instead of a straight geometric edge.  Because
	 *  `fbmVal` is evaluated at continuous world coords, the warp is spatially continuous
	 *  across the polygon boundary (the bug was that `u_splatRegion` only pointed at the region
	 *  when the sample was strictly inside, so the outside half of the feather band had
	 *  noise=0 and clipped the teeth at the edge).  Slot 0 (the base) keeps 0.  Absent → the
	 *  slot's noise is 0 (noiseless — the pre-P5 behaviour; a stale chunk renders a straight
	 *  edge rather than the old winner-attributed warp).  Parallel to `slotFeather`. */
	slotNoiseAmp?: number[];
	/** Set12 P5 (2026-08-27) — per-slot EDGE-NOISE scale in metres (the feature size of the
	 *  warp): slot i's value = the `edgeNoise.scale` of a region sharing that slot with a
	 *  non-zero amplitude (the scale of the highest-amplitude region; 1 = the shader's neutral).
	 *  See `slotNoiseAmp`.  Parallel to `slotNoiseAmp`. */
	slotNoiseScale?: number[];
	/** Set12 followup (2026-08-25) — per-slot SOFT-UNION opt-in (1 = this slot is a soft-union
	 *  participant): slot i's value = 1 iff EVERY region sharing that slot has `softUnion`
	 *  explicitly set (the "safe by construction" AND rule).  The fragment shader only smin-
	 *  unions an equal-dominance PAIR when BOTH slots are participants — so a far-but-bbox-
	 *  overlapping equal-dominance region in the adjacent slot can never be accidentally soft-
	 *  unioned with a slot (the "water replaces base" footgun).  Slot 0 (the base) stays 0 (never
	 *  smin'd).  Absent → the shader treats no slot as a soft-union participant (equal-dominance
	 *  pairs use the ordered paint-over — the safe default).  Parallel to `slotDominance`. */
	slotSoftUnion?: number[];
}

/** P3 (Set12): per-sample region-params values the shader needs to FEATHER each slot's distance
 *  per fragment — the region's blend band (feather), its falloff curve, its edge-noise
 *  amplitude, and whether it hard-overrides lower-dominance regions (opaque).  The values are
 *  stored alongside the region-modulated param values in the per-region params texture row, so
 *  a feather/curve edit is a params-texture rebuild (a handful of floats per region) with NO
 *  program recompile + NO splat re-rasterise (the region signature block owns that).
 *
 *  Column layout (per region row, MUST match the assembler's `REGION_PARAMS_COLUMNS`):
 *   col 0 = feather (metres; <= 0 = hard edge)
 *   col 1 = featherCurve: 0 = smoothstep, 1 = linear, 2 = step
 *   col 2 = edgeNoise amplitude (metres; 0 = none)
 *   col 3 = edgeNoise scale (metres)
 *   col 4 = opaque (1 = this region hard-overrides everything of lower dominance; the shader
 *           skips the weight lerp for lower-dominance slots — an opaque-region pixel never
 *           shows a trace of the underlying material, plan §7 GPU test 2)
 *   col 5+ = the recipe's regionModulated params (buildRegionParamLayouts)
 */
export const REGION_FEATHER_COLUMN = 0;
export const REGION_FEATHER_CURVE_COLUMN = 1;
export const REGION_NOISE_AMP_COLUMN = 2;
export const REGION_NOISE_SCALE_COLUMN = 3;
export const REGION_OPAQUE_COLUMN = 4;
/** The feather block's fixed width (the regionModulated param layouts start after it). */
export const REGION_FEATHER_BLOCK_COLUMNS = 5;

/** P2-era helper (Set12 §5 P2): convert a signed-distance SplatChunk to the RENORMALISED
 *  hard-weight form the Phase 5/6 shader path read (weight 1 in the winning slot, 0 elsewhere,
 *  sum 1) by thresholding each distance at 0.  P3 REPLACED this with the shader-side
 *  `regionWeight()` feather (the packer now stores the RAW distances — `packSplatChunk` no
 *  longer converts), so this is now DEAD code for the render path; it remains exported for the
 *  P2-era tests' historical assertions. */
export function splatDistancesToHardWeights(chunk: SplatChunk): {weights: Float32Array; regions: Uint8Array} {
	const {indices, weights, regions} = chunk;
	const count = indices.length;
	if (weights.length != count * SPLAT_MAX_MATERIALS) throw new Error("[SplatTypes] weights length must be indices.length * SPLAT_MAX_MATERIALS");
	const out = new Float32Array(count * SPLAT_MAX_MATERIALS);
	const outRegions = new Uint8Array(count);
	for (let i = 0; i < count; i++) {
		const idx = indices[i];
		// The winning slot: inside (d < 0) → weight 1.  All other slots (positive distance) → 0.
		// A sample that is inside NO region falls back to slot 0 (its idx is 0 and its d0 is +max
		// clamped — thresholded to 0 — so the fallback weight must be forced to 1 explicitly).
		const w = idx == 0 || (weights[i * SPLAT_MAX_MATERIALS + idx] ?? 0) < 0 ? 1 : 0;
		out[i * SPLAT_MAX_MATERIALS + idx] = w;
		outRegions[i] = w == 1 ? (regions?.[i] ?? 0) : 0;
	}
	return {weights: out, regions: outRegions};
}

/** Pack a SplatChunk into the splat DataTexture payloads: `indicesRGBA` (n×n×4 bytes: the ONE
 *  index per sample, replicated across channels — the shader only reads .r) + `weightsFloat`
 *  (n×n×8 floats: the ≤8 per-slot SIGNED DISTANCES in metres — the P3 shader's `u_splatDist`
 *  input; LinearFiltered, so the raw metres are interpolated across the feather band per
 *  fragment).  Set7 §9 — because an RGBA32F texel carries only 4 lanes, the 8 distances are
 *  emitted in LANE-SPLIT form: `weightsFloat` is `[d0..d3]` of every sample followed by
 *  `[d4..d7]` of every sample (two N×N "images" concatenated).  `buildSplatTextures` slices this
 *  into two N×N RGBA32F textures (`u_splatDist` slots 0–3 + `u_splatDist2` slots 4–7) — each
 *  LINEAR-filtered independently, preserving the per-lane cross-sample feather interpolation
 *  (a 2-texel-per-sample packed layout would break bilinear filtering across the feather band).
 *  Pure — tests exercise packing without GL. */
export function packSplatChunk(chunk: SplatChunk): {
	indicesRGBA: Uint8Array;
	/** Lane-split distances: [d0..d3 of every sample] ++ [d4..d7 of every sample] (length
	 *  count*8).  `buildSplatTextures` slices this into the two N×N RGBA32F textures. */
	weightsFloat: Float32Array;
	regionsRGBA?: Uint8Array;
} {
	const {indices, weights, recipeIDs, regions} = chunk;
	const n = Math.sqrt(indices.length);
	if (!Number.isInteger(n)) throw new Error("[SplatRasterizer] indices length must be a perfect square");
	if (weights.length != indices.length * SPLAT_MAX_MATERIALS) throw new Error("[SplatRasterizer] weights length must be indices.length * SPLAT_MAX_MATERIALS");
	if (recipeIDs.length > SPLAT_MAX_MATERIALS) throw new Error(`[SplatRasterizer] recipeIDs exceeds ${SPLAT_MAX_MATERIALS}`);
	const count = indices.length;
	const indicesRGBA = new Uint8Array(count * 4);
	// P3 (Set12): the chunk carries per-slot SIGNED DISTANCES — the packer stores them RAW (the
	// fragment shader feathers them per fragment via `regionWeight()` from the region params
	// texture, plan §3.6/§5-P3).  The P2 hard-weight threshold (`splatDistancesToHardWeights`)
	// is gone from the render path; the payload is the signed distance in metres, clamped to
	// ±(feather + noiseAmp) by the rasteriser (plan §3.3 step 3).  `weightsFloat` keeps its
	// historical name (the P2-era tests read it); its content is DISTANCES.
	//
	// Set7 §9 lane-split layout: the buffer is TWO N×N RGBA32F images concatenated.  Image A =
	// slots 0–3 (offset 0), image B = slots 4–7 (offset count*4).  Within each image the layout
	// is sample-major 4-wide: `weightsFloat[i*4 + s]` = slot s (<4) of sample i; image B is
	// `weightsFloat[count*4 + i*4 + (s-4)]`.  Both images are the SAME world lattice, so a
	// fragment's bilinear sample of both halves reads the SAME world point — no seam.  (The
	// existing slot 0–3 test reads `[i*4+s]` therefore remain valid unchanged.)
	const weightsFloat = new Float32Array(count * SPLAT_MAX_MATERIALS);
	for (let i = 0; i < count; i++) {
		for (let s = 0; s < SPLAT_MAX_MATERIALS; s++) {
			const v = weights[i * SPLAT_MAX_MATERIALS + s] ?? 0;
			if (s < 4) weightsFloat[i * 4 + s] = v;
			else weightsFloat[count * 4 + i * 4 + (s - 4)] = v;
		}
	}
	for (let i = 0; i < count; i++) {
		const idx = indices[i];
		if (idx < 0) throw new Error(`[SplatRasterizer] cannot pack a -1 index — weights must be renormalised first`);
		if (idx >= recipeIDs.length) throw new Error(`[SplatRasterizer] index ${idx} out of range for recipeIDs (${recipeIDs.length})`);
		indicesRGBA[i * 4 + 0] = idx;
		indicesRGBA[i * 4 + 1] = idx;
		indicesRGBA[i * 4 + 2] = idx;
		indicesRGBA[i * 4 + 3] = idx;
	}
	// Phase 7 — the per-sample region payload (RGBA8: redundant channels like the indices, so the
	// texelFetch read is a plain uvec4 compare; only .r is meaningful).  The winning region per
	// sample (map index + 1; 0 = base) rides along — it drives the params-texture row the
	// shader's `regionWeight()` reads its feather/curve/opaque from (P3).
	let regionsRGBA: Uint8Array | undefined;
	if (regions != null) {
		if (regions.length != count) throw new Error("[SplatRasterizer] regions length must equal indices length");
		regionsRGBA = new Uint8Array(count * 4);
		for (let i = 0; i < count; i++) {
			const r = regions[i];
			regionsRGBA[i * 4 + 0] = r;
			regionsRGBA[i * 4 + 1] = r;
			regionsRGBA[i * 4 + 2] = r;
			regionsRGBA[i * 4 + 3] = r;
		}
	}
	return {indicesRGBA, weightsFloat, regionsRGBA};
}
